/* ============================================================
   ProtoClinical: signature.js
   The cohesive "water / glass / dew" motion layer.
   Loads AFTER app.js. Reads window.__lenis for velocity.
   Everything here is gated behind reduced-motion + desktop and
   the merchant motion toggles (window.PC_CONFIG.motion).
   ============================================================ */
(function () {
  "use strict";

  var MOTION = (window.PC_CONFIG && window.PC_CONFIG.motion) || {};
  const REDUCED = (typeof window.PC_REDUCED === "boolean") ? window.PC_REDUCED
    : window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const DESKTOP = window.matchMedia("(hover: hover) and (pointer: fine)").matches && window.innerWidth > 1000;
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  let geometryVersion = 0;
  if (DESKTOP && !REDUCED) {
    const invalidateGeometry = () => { geometryVersion += 1; };
    window.addEventListener("scroll", invalidateGeometry, { capture: true, passive: true });
    window.addEventListener("resize", invalidateGeometry, { passive: true });
  }

  function pointerRect(el) {
    let rect = null, version = -1;
    const read = () => {
      if (!rect || version !== geometryVersion) {
        rect = el.getBoundingClientRect();
        version = geometryVersion;
      }
      return rect;
    };
    el.addEventListener("pointerenter", () => { rect = null; read(); });
    el.addEventListener("pointerleave", () => { rect = null; });
    return read;
  }

  function initMediaReveal() {
    if (REDUCED) return;
    const sel = ".pcard-media, .solve-card .ph, .post .ph, .post-media, .spot-media > .ph, .look-media, .ba, .concern-tile .ph, .mega-feature .ph, .pcard-media-link";
    const els = $$(sel);
    els.forEach((el) => el.classList.add("media-reveal"));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("media-in"); io.unobserve(e.target); } });
    }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
    els.forEach((el) => io.observe(el));
  }

  function initShimmerAndHairline() {
    if (REDUCED) return;
    const badges = $$(".badge");
    badges.forEach((b) => b.classList.add("shimmer"));
    const bio = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("go"); bio.unobserve(e.target); } });
    }, { threshold: 0.6 });
    badges.forEach((b) => bio.observe(b));

    const sections = $$("main > .shopify-section > section.section, main > section.section");
    sections.forEach((s) => s.classList.add("hairline"));
    const hio = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("drawn"); hio.unobserve(e.target); } });
    }, { threshold: 0.05 });
    sections.forEach((s) => hio.observe(s));
  }

  function initTilt() {
    if (REDUCED || !DESKTOP) return;
    const tiles = $$(".concern-tile, .ing-fam, .brand-cell, .solve-card, .brand-logo-card");
    tiles.forEach((t) => {
      t.classList.add("tilt");
      let raf = null, tx = 0, ty = 0;
      const readRect = pointerRect(t);
      t.addEventListener("pointermove", (e) => {
        const r = readRect();
        const px = (e.clientX - r.left) / (r.width || 1) - 0.5;
        const py = (e.clientY - r.top) / (r.height || 1) - 0.5;
        tx = clamp(-py * 9, -9, 9); ty = clamp(px * 9, -9, 9);
        if (!raf) raf = requestAnimationFrame(() => { t.style.transform = `perspective(800px) rotateX(${tx}deg) rotateY(${ty}deg) translateZ(0)`; raf = null; });
      });
      t.addEventListener("pointerleave", () => {
        if (raf !== null) cancelAnimationFrame(raf);
        raf = null;
        t.style.transform = "";
      });
    });
  }

  function initCursor() {
    if (REDUCED || !DESKTOP) return;
    const glow = $("#cursorGlow");
    const allowGlow = MOTION.cursorGlow !== false && glow;
    const allowMagnetic = MOTION.magnetic !== false;
    const zones = $$("[data-glow-zone]");
    const bloomers = ".pcard, .btn, [data-magnetic], .concern-tile, .brand-cell, .hotspot";
    let tx = innerWidth / 2, ty = innerHeight / 2, cx = tx, cy = ty, active = false;
    let lastRipple = 0;

    if (allowGlow) {
      let rafId = null;
      // Pause rAF loop when cursor glow is inactive and coordinates have converged to save CPU frames
      function startRaf() {
        if (rafId === null) rafId = requestAnimationFrame(step);
      }
      function step() {
        cx += (tx - cx) * 0.16; cy += (ty - cy) * 0.16;
        glow.style.transform = `translate(${cx}px, ${cy}px) translate(-50%, -50%)`;
        if (Math.abs(tx - cx) > 0.1 || Math.abs(ty - cy) > 0.1) {
          rafId = requestAnimationFrame(step);
        } else {
          rafId = null;
        }
      }

      function onMove(e) {
        tx = e.clientX; ty = e.clientY;
        const now = performance.now();
        if (active && now - lastRipple > 220) { lastRipple = now; spawnRipple(e.clientX, e.clientY); }
        startRaf();
      }
      zones.forEach((z) => {
        z.addEventListener("pointerenter", () => { active = true; glow.setAttribute("data-on", ""); document.body.classList.add("cursor-active"); startRaf(); });
        z.addEventListener("pointerleave", (e) => {
          if (e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest("[data-glow-zone]")) return;
          active = false; glow.removeAttribute("data-on"); glow.removeAttribute("data-bloom"); document.body.classList.remove("cursor-active");
        });
        z.addEventListener("pointermove", onMove);
      });
      document.addEventListener("pointerover", (e) => {
        if (!active) return;
        if (e.target.closest(bloomers)) glow.setAttribute("data-bloom", "");
        else glow.removeAttribute("data-bloom");
      });
    }

    $$("[data-magnetic]").forEach((el) => {
      const strength = parseFloat(el.dataset.magnetic) || 0.25;
      if (allowMagnetic) {
        const readRect = pointerRect(el);
        el.addEventListener("pointermove", (e) => {
          const r = readRect();
          const mx = e.clientX - (r.left + r.width / 2);
          const my = e.clientY - (r.top + r.height / 2);
          el.style.transform = `translate(${mx * strength}px, ${my * strength}px)`;
        });
        el.addEventListener("pointerleave", () => { el.style.transform = ""; });
      }
      el.addEventListener("pointerdown", () => el.classList.add("pressing"));
      window.addEventListener("pointerup", () => el.classList.remove("pressing"));
    });
    document.addEventListener("pointerdown", (e) => {
      if (e.target.closest(".btn, .hotspot button, .quick-add, [data-magnetic]")) spawnRipple(e.clientX, e.clientY);
    });
  }

  function spawnRipple(x, y) {
    if (REDUCED) return;
    const r = document.createElement("div");
    r.className = "cursor-ripple";
    r.style.left = x + "px"; r.style.top = y + "px";
    document.body.appendChild(r);
    requestAnimationFrame(() => r.classList.add("go"));
    setTimeout(() => r.remove(), 850);
  }

  function initVelocity() {
    if (REDUCED) return;
    const lenis = window.__lenis;
    if (!lenis) return;
    const els = $$(".carousel-track .pcard, .spot-media");
    els.forEach((el) => el.classList.add("vel-skew"));
    const inView = new Set();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { e.isIntersecting ? inView.add(e.target) : inView.delete(e.target); });
    }, { rootMargin: "10% 0px" });
    els.forEach((el) => io.observe(el));
    let cur = 0;
    let rafId = null;

    // Pause rAF loop when scroll velocity drops to zero and lerping finishes
    function step() {
      const v = clamp((lenis.velocity || 0) * 0.06, -3.4, 3.4);
      cur += (v - cur) * 0.12;
      const absCur = Math.abs(cur);
      const absV = Math.abs(v);
      if (absCur > 0.04 || absV > 0.04) {
        const sk = cur.toFixed(2);
        const sc = (1 + Math.min(0.02, absCur * 0.006)).toFixed(3);
        inView.forEach((el) => { el.style.transform = `skewY(${sk}deg) scale(${sc})`; });
      } else {
        inView.forEach((el) => { if (el.style.transform) el.style.transform = ""; });
      }
      if (absCur > 0.005 || absV > 0.005) {
        rafId = requestAnimationFrame(step);
      } else {
        cur = 0;
        rafId = null;
      }
    }

    function startRaf() {
      if (rafId === null) rafId = requestAnimationFrame(step);
    }

    lenis.on("scroll", startRaf);
  }

  function initMarquees() {
    if (REDUCED) return;
    const lenis = window.__lenis;
    const tracks = $$(".marquee-track");
    if (!tracks.length) return;
    // Precompute halfWidth outside the requestAnimationFrame loop to avoid
    // forced synchronous layout recalculation (reflows) on every frame.
    const state = tracks.map((t) => ({
      el: t,
      x: 0,
      hovered: false,
      halfWidth: (t.scrollWidth / 2) || 1,
    }));
    state.forEach((s) => {
      const wrap = s.el.closest(".marquee");
      if (wrap) {
        wrap.addEventListener("pointerenter", () => { s.hovered = true; });
        wrap.addEventListener("pointerleave", () => { s.hovered = false; });
      }
    });
    const updateWidths = () => {
      state.forEach((s) => { s.halfWidth = (s.el.scrollWidth / 2) || 1; });
    };
    window.addEventListener("resize", updateWidths, { passive: true });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(updateWidths);
    }

    let lastY = window.scrollY, dir = 1;
    function readSpeed() {
      const v = lenis ? (lenis.velocity || 0) : (window.scrollY - lastY);
      lastY = window.scrollY;
      if (Math.abs(v) > 0.5) dir = v > 0 ? 1 : -1;
      return Math.min(2.2, 0.45 + Math.abs(v) * 0.02);
    }
    (function raf() {
      const speed = readSpeed();
      state.forEach((s) => {
        if (s.hovered) return;
        const w = s.halfWidth || 1;
        s.x -= dir * speed;
        if (s.x <= -w) s.x += w; if (s.x > 0) s.x -= w;
        s.el.style.transform = `translateX(${s.x}px)`;
      });
      requestAnimationFrame(raf);
    })();
    tracks.forEach((t) => { t.style.animation = "none"; });
  }

  function initSpotlightAssemble() {
    $$(".spotlight").forEach((spot) => {
      spot.classList.add("spot-assemble");
      if (REDUCED) { spot.classList.add("assembled"); return; }
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { spot.classList.add("assembled"); io.disconnect(); } });
      }, { threshold: 0.3 });
      io.observe(spot);
    });
  }

  function initBanner() {
    if (REDUCED || !DESKTOP) return;
    $$(".banner").forEach((banner) => {
      let last = 0;
      banner.addEventListener("pointermove", (e) => {
        const now = performance.now();
        if (now - last > 140) { last = now; spawnRipple(e.clientX, e.clientY); }
      });
    });
    $$(".ba").forEach((ba) => {
      const handle = $(".ba-handle", ba);
      if (!handle) return;
      ba.addEventListener("pointerup", () => {
        handle.style.transition = "left .5s cubic-bezier(.34,1.56,.64,1)";
        setTimeout(() => { handle.style.transition = ""; }, 520);
      });
      ba.addEventListener("pointerdown", () => { handle.style.transition = "none"; });
    });
  }

  function initCartPlop() {
    const cartBtn = $("#cartBtn");
    window.addEventListener("pc:add", (e) => {
      const badge = $(".cart-count");
      if (badge) { badge.classList.remove("pop"); void badge.offsetWidth; badge.classList.add("pop"); }
      if (REDUCED || !cartBtn) return;
      const from = e.detail && e.detail.from;
      const sr = from ? from.getBoundingClientRect() : { left: innerWidth / 2, top: innerHeight / 2, width: 0, height: 0 };
      const tr = cartBtn.getBoundingClientRect();
      const sx = sr.left + sr.width / 2, sy = sr.top + sr.height / 2;
      const dx = (tr.left + tr.width / 2) - sx, dy = (tr.top + tr.height / 2) - sy;
      const drop = document.createElement("div");
      drop.className = "plop droplet";
      drop.innerHTML = window.__dropletSVG ? window.__dropletSVG() : "";
      drop.style.left = sx + "px"; drop.style.top = sy + "px";
      drop.style.setProperty("--dx", dx + "px");
      drop.style.setProperty("--dy", dy + "px");
      document.body.appendChild(drop);
      requestAnimationFrame(() => drop.classList.add("go"));
      setTimeout(() => drop.remove(), 760);
    });
  }

  function initDewy() {
    if (REDUCED) return;
    if (DESKTOP) {
      $$(".hero-img .ph, .banner-layer .ph, .ba .ba-after .ph").forEach((el) => el.classList.add("dewy"));
    }
    $$(".pcard-media .ph, .spot-media > .ph").forEach((el) => el.classList.add("ripple-hover"));
  }

  function boot() {
    initDewy();
    initMediaReveal();
    initShimmerAndHairline();
    initTilt();
    initCursor();
    initVelocity();
    initMarquees();
    initSpotlightAssemble();
    initBanner();
    initCartPlop();
  }

  window.addEventListener("pc:ready", boot, { once: true });
  setTimeout(() => { if (!window.__pcReady) boot(); }, 4000);
})();
